import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { format } from 'date-fns';
import { toNumber } from 'ng-zorro-antd/core/util';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Coupon } from 'src/app/core/interfaces/coupon';
import { HttpRequestService, LocalStorageService } from 'src/app/core/services';
import { RejectionReasonComponent } from 'src/app/shared/components/rejection-reason/rejection-reason.component';

@Component({
  selector: 'app-coupons',
  templateUrl: './coupons.component.html',
  styleUrls: ['./coupons.component.scss'],
})
export class CouponsComponent implements OnInit {
  allCoupons: Coupon[] = [];
  allApprovedCoupons: Coupon[] = [];
  isAddOrUpdate: boolean = true;
  totalDataCount = 0;
  totalApprovedDataCount = 0;
  pageSize = 12;
  pageIndex = 1;
  loading = false;
  searchCoupon: Subject<string> = new Subject<string>();
  search = '';
  userRole: string = '';
  subAdminAddedCoupon: Coupon[] = [];
  startDate: any;
  endDate: any;
  dateRange: any;
  index: number = 0;
  skipIndex: number = 0;
  snapShootIndex: number = 0;
  predefinedColors: string[] = ['magenta', 'blue', 'green', 'red', 'purple'];
  expiredCoupons: Coupon[] = [];
  totalExpiredDataCount = 0;
  constructor(
    private httpRequestService: HttpRequestService,
    private notificationService: NzNotificationService,
    private localStorageService: LocalStorageService,
    private modalService: NzModalService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {
    this.snapShootIndex = toNumber(
      this.activatedRoute.snapshot.queryParams.index
    );
    this.userRole = this.localStorageService.getItem('user').role;
    if (this.snapShootIndex) {
      this.changeIndex(this.snapShootIndex);
    }
  }

  ngOnInit(): void {
    this.searchCoupon.pipe(debounceTime(1000)).subscribe((success: string) => {
      this.search = success;
      this.getallCoupons(this.index);
    });
    this.getRandomColor();
  }

  getRandomColor(): string {
    const randomIndex = Math.floor(
      Math.random() * this.predefinedColors.length
    );
    // Use setTimeout to defer execution until the next tick

    return this.predefinedColors[randomIndex];
  }
  onChangeDateRange(date: any): void {
    if (!date || date.length === 0) {
      this.startDate = null;
      this.endDate = null;
    } else {
      this.startDate = format(date[0], 'yyyy-MM-dd');
      this.endDate = format(date[1], 'yyyy-MM-dd');
    }

    this.getallCoupons(this.index);
  }
  changeIndex(data: any) {
    this.router.navigate([], { queryParams: { index: data } });

    this.index = data;
    this.pageSize = 12;
    this.pageIndex = 1;
    this.skipIndex = 0;
    if (data === 0) {
      this.allApprovedCoupons = [];
    } else if (data === 1) {
      this.allCoupons = [];
    } else if (data === 2) {
      this.expiredCoupons = [];
    }

    this.getallCoupons(data, this.skipIndex);
  }

  /* Get all coupons */
  getallCoupons(index: number = 0, skip = 0, sortBy?: any): void {
    index = index ?? this.index;
    let params: any = { skip: this.skipIndex, limit: this.pageSize };

    if (this.search) {
      params.search = this.search;
    }
    if (sortBy) {
      params.sortBy = JSON.stringify(sortBy);
    }
    if (this.startDate && this.endDate) {
      params.startDate = this.startDate;
      params.endDate = this.endDate;
    } else {
      delete params.startDate;
      delete params.endDate;
    }

    const today = new Date();

    if (index === 0) {
      params.approveStatus = 'approved';
    } else if (index === 1 && this.userRole === 'admin') {
      params.approveStatus = 'pending';
    } else if (index === 1 && this.userRole === 'subAdmin') {
      params.approveStatus = ['pending', 'rejected'];
    } else if (index === 2) {
      // expired tab
      params.expired = true;
    } else {
      delete params.approveStatus;
    }

    this.loading = true;
    this.httpRequestService.request('get', 'coupon-codes', params).subscribe(
      (result) => {
        this.loading = false;
        const data: Coupon[] = result.data;
        if (index === 0) {
          this.allApprovedCoupons = data;
          this.totalApprovedDataCount = result.totalCount;
        } else if (index === 1) {
          this.allCoupons = data;
          this.totalDataCount = result.totalCount;
        } else if (index === 2) {
          this.expiredCoupons = data;
          this.totalExpiredDataCount = result.totalCount;
        }
      },
      (err) => {
        this.loading = false;
      }
    );
  }

  deleteCoupon(id: any) {
    this.httpRequestService
      .request('delete', `coupon-codes/${id}`)
      .subscribe((result: any) => {
        this.notificationService.success('', 'Coupon Deleted Successfully');
        this.getallCoupons(this.index);
      });
  }
  /* For Pagination / Sending skip */
  onQueryParamsChange(params: NzTableQueryParams): void {
    const { pageSize, pageIndex } = params;
    this.pageSize = pageSize;
    this.pageIndex = pageIndex;
    this.skipIndex = pageSize * (pageIndex - 1);

    // Reset only the relevant data based on active tab
    if (this.index === 0) {
      this.allApprovedCoupons = [];
    } else if (this.index === 1) {
      this.allCoupons = [];
    } else if (this.index === 2) {
      this.expiredCoupons = [];
    }

    this.getallCoupons(this.index, this.skipIndex);
  }

  /* For Pagination / Sending skip for grid view*/
  /* Status change*/
  updateStatus(id: string, status: boolean): void {
    this.httpRequestService
      .request('put', `coupon-codes/active-inactive/${id}`, {
        status: !status,
      })
      .subscribe((result: any) => {
        this.notificationService.success('', 'Status Updated Successfully');
        this.getallCoupons(this.index);
      });
  }
  updatePendingStatus(id: string, pendingStatus: string) {
    this.httpRequestService
      .request('put', `coupon-codes/approved-rejected/${id}`, {
        approveStatus: 'approved',
      })
      .subscribe((result: any) => {
        this.notificationService.success('', 'Status Updated Successfully');
        this.getallCoupons(this.index);
      });
  }
  openRejectionReason(id: string, pendingStatus: string) {
    const rejectionReason = this.modalService.create({
      nzTitle: 'Add A Reason',
      nzContent: RejectionReasonComponent,
      nzWidth: '800px',
      nzFooter: null,
      nzComponentParams: {
        updateId: id,
        component: 'coupon-codes',
      },
      nzCancelText: null,
      nzOkText: null,
    });

    const questionService = rejectionReason.afterClose.subscribe(
      (data: any) => {
        this.getallCoupons(this.index);
      }
    );
  }

  openAnalytics(id: string): void {
    this.router.navigate(['main/coupons/analytics', id]);
  }

  // Search by coupon name or code
  searchCouponByNameCode(event: any): void {
    this.searchCoupon.next(event);
  }
}
